import { isGroupEntry } from 'BMapsRedux/projectState/entry';
import {
    FragmentSearchResult, BestPoseBindingInfo, sortFrags, poseExchemP,
} from '../../../model/FragmentSearchResult';
import { getFragmentPropsForSuggestion, getMapCaseAndTargetInfo } from './utils';

/**
 * @typedef {import('BMapsModel').DecodedFragment} DecodedFragment
 * @typedef {import('BMapsModel').FragmentSearchResult} FragmentSearchResult
 * @typedef {{ frag: DecodedFragment, fragIndex: number, displayIndex: number }} DisplayPose
 * @typedef {{ value: any, filter: function(DecodedFragment): boolean }} PosePropertyFilter
 */

/**
 * UI-layer wrapper around a {@link FragmentSearchResult} (from bfd-server or dataservice).
 *
 * Each instance represents one (fragment, modType) pair. In fragment-growing mode
 * there may be multiple instances sharing the same fragment name, one per link type.
 *
 * Enriches the raw result with:
 *   - fragInfo: fragment chemical properties (MW, SMILES, etc.)
 *   - perCaseInfo: per-protein {@link AnnotatedBestPoseInfo} with target context
 *   - summaryInfo: cross-protein selectivity metrics (active counts, score)
 *   - displayPoses / selectedIndex: pose explorer state for the UI slider
 *   - primaryScoreField: active scoring field ('enSolute' | 'bindingScore'),
 *     which drives table sort, score ratio, and activity highlight threshold
 */
export class AnnotatedSearchResult {
    static getProjectCasesForSuggestions(suggestions) {
        const projectCases = new Set();

        for (const sugg of suggestions) {
            sugg.projectCases.forEach((pc) => projectCases.add(pc));
        }
        return [...projectCases];
    }

    /**
     * The projectCases that get their own per-protein TABLE column: loaded structures and
     * individual structures of interest, but NOT curated-panel (group) members — those would
     * add a column per library structure (hundreds). Library members still appear on the
     * coral/kinome map (via getInfoForCoralMap).
     * @param {FragmentSearchResult[]} suggestions
     * @returns {string[]}
     */
    static getColumnProjectCasesForSuggestions(suggestions) {
        return this.getProjectCasesForSuggestions(suggestions)
            .filter((pc) => !isGroupEntry(getMapCaseAndTargetInfo(pc).targetInfo));
    }

    /**
     * @param {FragmentSearchResult} suggestion
     * @param {ActivityThresholdInfo} [activityThresholdInfo]
     */
    constructor(suggestion, activityThresholdInfo) {
        /** @type {FragmentSearchResult} */
        this.suggestion = suggestion;
        /** @type {string[]} */
        this.projectCases = Object.keys(suggestion.projectCaseDetails);
        this.fragInfo = getFragmentPropsForSuggestion(this.suggestion, this.projectCases);
        /** @type {AnnotatedBestPoseInfo?} */
        this.refBestPoseInfo = null;
        /** @type {{ [projectCase: string]: AnnotatedBestPoseInfo }} */
        this.perCaseInfo = {};
        this.updatePerCaseInfo();
        this.updateSummaryInfo(activityThresholdInfo);
        /** @type {DisplayPose[]} */
        this.displayPoses = [];
        this.assignDisplayPoses(this.frags);
        /** @type {number} */
        this.selectedIndex = -1;
        /** @type {string} */
        this.primaryScoreField = 'enSolute';
        /** @type {number} Count of poses surviving the global primary-score
         *  filter (applied before per-row `displayFilters`). Defaults to the
         *  total pose count when no global filter is active. Drives row
         *  visibility and pose-count display in the parent table — keeping
         *  it independent of `displayFilters` ensures per-row filters can
         *  empty `displayPoses` without hiding the row. */
        this.globalFilteredCount = this.frags.length;
    }

    // Passthroughs to the underlying result object
    get origin() { return this.suggestion.origin; }
    get modType() { return this.suggestion.modType; }
    get fragAtomName() { return this.suggestion.fragAtomName; }
    get frags() { return this.suggestion.frags; }
    set frags(value) { this.suggestion.frags = value; }
    get name() { return this.suggestion.name; }
    get mwt() { return this.suggestion.mwt; }
    get selectedFrag() { return this.frags[this.selectedIndex]; }
    get selectedDisplayPose() { return this.getDisplayPoseByFragIndex(this.selectedIndex); }
    get selectedDisplayIndex() { return this.selectedDisplayPose?.displayIndex ?? -1; }

    getFragByIndex(index) {
        return this.suggestion.frags[index];
    }

    getFragByDisplayIndex(displayIndex) {
        const displayPose = this.displayPoses[displayIndex];
        return displayPose?.frag;
    }

    getDisplayPoseByFragIndex(fragIndex) {
        const index = this.displayPoses.findIndex((d) => d.fragIndex === fragIndex);
        return this.displayPoses[index];
    }

    /**
     * Pick the best pose for each projectCase using the given field, then rebuild perCaseInfo.
     * Should be called before recalculateDisplayPoses when the scoring field changes.
     * @param {{ bestPoseOrderField?: string }} [options]
     */
    recalculateBestPoses({ bestPoseOrderField }={}) {
        this.suggestion.recalculateBestPoses(bestPoseOrderField);
        this.updatePerCaseInfo();
    }

    /**
     * Display poses are the visible poses that can be viewed by the selector,
     * potentially filtered and sorted. They need to be recalculated whenever the
     * view parameters change.
     * The difference between sortPosesBy and bestPoseOrderField is that
     * sortPosesBy is used to sort the display poses, and bestPoseOrderField
     * is used to determine which pose is providing the values for the row
     * in the table. For "position" sorting, the best pose is at index 0.
     *
     * `globalFilter`, when supplied, is applied **before** `displayFilters`
     * and its post-filter survivor count is recorded on `this.globalFilteredCount`.
     * The parent table reads that count for row visibility and pose-count
     * display so per-row `displayFilters` cannot hide a row by themselves.
     * Pass `null` (default) to leave the global filter inactive and
     * `globalFilteredCount` equal to `this.frags.length`.
     * @param {{
     *     sortPosesBy: string,
     *     bestPoseOrderField: string,
     *     displayFilters: {[propName: string]: PosePropertyFilter},
     *     globalFilter: PosePropertyFilter | null,
     *     preserveDisplayIndex: boolean,
     * }} param0
     */
    recalculateDisplayPoses({
        sortPosesBy, bestPoseOrderField, displayFilters={}, globalFilter=null,
        preserveDisplayIndex=false,
    }) {
        const {
            frag: oldSelectedFrag, displayIndex: oldDisplayIndex,
        } = this.selectedDisplayPose ?? {};

        const afterGlobal = globalFilter
            ? this.frags.filter(globalFilter.filter)
            : this.frags;
        this.globalFilteredCount = afterGlobal.length;
        const filtered = this.filterPoses(afterGlobal, displayFilters);
        const sorted = sortFrags(filtered, { sortPosesBy, bestPoseOrderField });
        this.assignDisplayPoses(sorted);

        // Update selectedIndex if necessary
        if (!oldSelectedFrag) {
            // No previously selected frag, so just try to select the first one
            this.selectedIndex = this.displayPoses[0]?.fragIndex ?? -1;
        } else if (!filtered.includes(oldSelectedFrag)) {
            // No longer have the previously selected frag, but try to use the old display index
            let newDisplayIndex = oldDisplayIndex;
            if (newDisplayIndex >= this.displayPoses.length) {
                newDisplayIndex = this.displayPoses.length - 1;
            }
            this.selectedIndex = this.displayPoses[newDisplayIndex]?.fragIndex ?? -1;
        }

        if (preserveDisplayIndex) {
            // Try to preserve the display index, but if it doesn't exist, select the first one
            const newDisplayPose = this.displayPoses[oldDisplayIndex];
            if (newDisplayPose) {
                this.selectedIndex = newDisplayPose.fragIndex;
            } else {
                this.selectedIndex = this.displayPoses[0]?.fragIndex ?? -1;
            }
        }
    }

    /**
     *
     * @param {DecodedFragment[]} poses
     * @param {{[propName: string]: PosePropertyFilter}} filters
     * @returns {DecodedFragment[]}
     */
    filterPoses(poses, filters={}) {
        return Object.values(filters).reduce((acc, next) => acc.filter(next.filter), poses);
    }

    /**
     * @param {DecodedFragment[]} orderedFrags
     * @returns {DisplayPose[]}
     */
    assignDisplayPoses(orderedFrags) {
        this.displayPoses = orderedFrags.map((frag, displayIndex) => {
            const fragIndex = this.frags.findIndex((f) => f === frag);
            return { frag, fragIndex, displayIndex };
        });
    }

    /**
     * Rebuild perCaseInfo and refBestPoseInfo from the underlying suggestion's
     * projectCaseDetails. refBestPoseInfo is set to the first projectCase's entry
     * (insertion order), which serves as the reference denominator for scoreRatio.
     */
    updatePerCaseInfo() {
        this.refBestPoseInfo = null;
        for (const [projectCase, poseInfo] of Object.entries(this.suggestion.projectCaseDetails)) {
            const newInfo = new AnnotatedBestPoseInfo(poseInfo.bestPoseInfo, projectCase, this);
            if (!this.refBestPoseInfo) {
                this.refBestPoseInfo = newInfo;
            }
            this.perCaseInfo[projectCase] = newInfo;
        }
    }

    /**
     * Compute cross-protein selectivity metrics for this fragment.
     * Finds each protein's best pose (using thresholdInfo.bestPoseOrderField) and
     * classifies it as active/inactive × target/off-target.
     * @param {ActivityThresholdInfo} [thresholdInfo]
     * @returns {{ activeTargetCount: number, activeOffTargetCount: number,
     *             selectivityScore: number, selectivityRatio: number }}
     */
    getSummaryInfo(thresholdInfo=new ActivityThresholdInfo()) {
        const { bestPoseOrderField, activityField, activityThreshold } = thresholdInfo;
        const activeTargets = [];
        const activeOffTargets = [];
        const inactiveTargets = [];
        const inactiveOffTargets = [];

        let scoredCaseCount = 0;
        for (const projectCase of this.projectCases) {
            const { targetInfo } = this.perCaseInfo[projectCase];
            // Curated (reference) panel members are context only — shown on the coral map
            // but excluded from the selectivity partition AND the ratio denominator.
            if (isGroupEntry(targetInfo)) continue;
            scoredCaseCount += 1;
            const { isTarget } = targetInfo;
            const bindingInfo = FragmentSearchResult.findBestPose(this.suggestion.frags, {
                projectCase, bestPoseOrderField,
            });
            if (bindingInfo) {
                let arrayToUse;
                if (bindingInfo.meetsThreshold(activityThreshold, activityField)) {
                    arrayToUse = isTarget ? activeTargets : activeOffTargets;
                } else {
                    arrayToUse = isTarget ? inactiveTargets : inactiveOffTargets;
                }
                arrayToUse.push({ projectCase, bindingInfo });
            } else {
                // What should we do about the no-data cases?
            }
        }

        return {
            activeTargetCount: activeTargets.length,
            activeOffTargetCount: activeOffTargets.length,
            selectivityScore: (
                activeTargets.length + inactiveOffTargets.length - activeOffTargets.length
            ),
            selectivityRatio: (
                scoredCaseCount > 0
                    ? (activeTargets.length + inactiveOffTargets.length) / scoredCaseCount
                    : 0
            ),
        };
    }

    /** @param {ActivityThresholdInfo?} */
    updateSummaryInfo(thresholdInfo) {
        this.summaryInfo = this.getSummaryInfo(thresholdInfo);
    }
}

/**
 * Per-protein best-pose data for one (fragment, projectCase) pair.
 *
 * Wraps a {@link BestPoseBindingInfo} from the raw search result and adds
 * target/off-target context from the loaded protein set. One instance lives in
 * {@link AnnotatedSearchResult#perCaseInfo} for each projectCase.
 *
 * Note: this.suggestion is the parent {@link AnnotatedSearchResult},
 * not the underlying FragmentSearchResult.
 */
export class AnnotatedBestPoseInfo {
    /**
     * @param {BestPoseBindingInfo} bestPoseInfo
     * @param {string} projectCase
     * @param {AnnotatedSearchResult} suggestion - parent suggestion; used for scoreRatio
     */
    constructor(bestPoseInfo, projectCase, suggestion) {
        this.bestPoseInfo = bestPoseInfo;
        this.projectCase = projectCase;
        this.suggestion = suggestion;
        const { mapCase, targetInfo } = getMapCaseAndTargetInfo(projectCase);
        this.mapCase = mapCase;
        this.targetInfo = targetInfo;
    }

    isActive({ activityThreshold, activityField }=new ActivityThresholdInfo()) {
        return this.bestPoseInfo?.meetsThreshold(activityThreshold, activityField) || false;
    }

    isActiveTarget(thresholdInfo) {
        return !isGroupEntry(this.targetInfo) && this.targetInfo.isTarget
            && this.isActive(thresholdInfo);
    }

    isActiveOffTarget(thresholdInfo) {
        return !isGroupEntry(this.targetInfo) && !this.targetInfo.isTarget
            && this.isActive(thresholdInfo);
    }

    get bindingScore() { return this.bestPoseInfo?.bindingScore; }
    get ligandEfficiency() { return this.bestPoseInfo?.ligandEfficiency; }
    get enSolute() { return this.bestPoseInfo?.enSolute; }
    get ligandEfficiencyEn() { return this.bestPoseInfo?.ligandEfficiencyEn; }
    get enSolv() { return this.bestPoseInfo?.enSolv; }
    get exchemPotential() { return this.bestPoseInfo?.exchemPotential; }

    get poseStatistics() {
        const underlyingSuggestion = this.suggestion.suggestion;
        return underlyingSuggestion.projectCaseDetails[this.projectCase]?.poseStatistics ?? {};
    }

    /**
     * Score for this protein divided by the score for the reference (first) protein,
     * using the parent suggestion's primaryScoreField. Returns null if either value
     * is unavailable. The reference protein is typically the primary target.
     * @type {number|null}
     */
    get scoreRatio() {
        const field = this.suggestion.primaryScoreField || 'enSolute';
        const myValue = this[field];
        const refValue = this.suggestion.refBestPoseInfo?.[field];
        if (myValue == null || refValue == null) {
            return null;
        }
        return myValue / refValue;
    }

    filterBucketPoses(projectCase, inclusiveMin, exclusiveMax) {
        return this.suggestion.frags.filter((frag) => {
            // gen2 poses have no binding score → excluded from binding-score buckets.
            const exchemP = poseExchemP(frag);
            if (exchemP == null) return false;
            const bindingScore = BestPoseBindingInfo.getBindingScore(exchemP);
            return frag.projectCase === projectCase
                && (exclusiveMax == null || bindingScore < exclusiveMax)
                && (inclusiveMin == null || bindingScore >= inclusiveMin);
        });
    }

    bucketCount(projectCase, inclusiveMin, exclusiveMax) {
        if (this.bindingScore == null) return null;
        return this.filterBucketPoses(projectCase, inclusiveMin, exclusiveMax).length;
    }

    get poseBucket0_1() { return this.bucketCount(this.projectCase, -1, 0); }
    get poseBucket1_2() { return this.bucketCount(this.projectCase, -2, -1); }
    get poseBucket2_3() { return this.bucketCount(this.projectCase, -3, -2); }
    get poseBucket3_4() { return this.bucketCount(this.projectCase, -4, -3); }
    get poseBucket4_5() { return this.bucketCount(this.projectCase, -5, -4); }
    get poseBucket5_6() { return this.bucketCount(this.projectCase, -6, -5); }
    get poseBucket6_7() { return this.bucketCount(this.projectCase, -7, -6); }
    get poseBucket7_8() { return this.bucketCount(this.projectCase, -8, -7); }
    get poseBucket8_9() { return this.bucketCount(this.projectCase, -9, -8); }
    get poseBucket9_10() { return this.bucketCount(this.projectCase, -10, -9); }
    get poseBucket10_11() { return this.bucketCount(this.projectCase, -11, -10); }
    get poseBucket11_12() { return this.bucketCount(this.projectCase, -12, -11); }
    get poseBucket12_13() { return this.bucketCount(this.projectCase, -13, -12); }
    get poseBucket13_14() { return this.bucketCount(this.projectCase, -14, -13); }
    get poseBucket14_15() { return this.bucketCount(this.projectCase, -15, -14); }
    get poseBucket15_16() { return this.bucketCount(this.projectCase, -16, -15); }
    get poseBucket16_17() { return this.bucketCount(this.projectCase, -17, -16); }
    get poseBucket17_18() { return this.bucketCount(this.projectCase, -18, -17); }
    get poseBucket18_19() { return this.bucketCount(this.projectCase, -19, -18); }
    get poseBucket19_20() { return this.bucketCount(this.projectCase, -20, -19); }
    get poseBucketX_0() { return this.bucketCount(this.projectCase, 0, undefined); }
    get poseBucket20_X() { return this.bucketCount(this.projectCase, undefined, -20); }
}

/**
 * Threshold parameters for the activity highlight feature (green/red row coloring).
 *
 * Controls which energy field is compared and at what cutoff a fragment is
 * considered "active". Passed into getSummaryInfo and BestPoseBindingInfo.meetsThreshold.
 *
 * Should be reconstructed (not mutated) when primaryScoreField changes, so that
 * activityField stays in sync with the rest of the scoring configuration.
 */
export class ActivityThresholdInfo {
    static get DefaultBindingScoreThreshold() { return -7.0; }
    static get DefaultEnSoluteThreshold() { return -19.0; }

    /**
     * @param {{ activityField?: string, activityThreshold?: number,
     *           bestPoseOrderField?: string }} [thresholdInfo]
     *   activityField: field to compare against threshold ('enSolute'|'bindingScore';
     *     default 'bindingScore')
     *   activityThreshold: cutoff value; if omitted, uses -19 for enSolute, -7 for bindingScore
     *   bestPoseOrderField: field used to select best pose in getSummaryInfo
     *     (default 'bindingScore'; ideally matches activityField)
     */
    constructor(thresholdInfo={}) {
        const { bestPoseOrderField='bindingScore', activityField='bindingScore' } = thresholdInfo;
        let { activityThreshold } = thresholdInfo;
        if (activityThreshold == null) {
            const defaults = {
                bindingScore: ActivityThresholdInfo.DefaultBindingScoreThreshold,
                enSolute: ActivityThresholdInfo.DefaultEnSoluteThreshold,
            };
            activityThreshold = defaults[activityField] ?? 0;
        }
        this.bestPoseOrderField = bestPoseOrderField;
        this.activityField = activityField;
        this.activityThreshold = activityThreshold;
    }
}
